import { FC, useMemo } from 'react';
import { cn, getDeviationAmountColor } from 'src/utils/utility';
import { getHoverShadowStyles } from './cardStyles';
import { CardSkeleton } from './CardSkeleton';
import { CardTheme, MetricConfig } from './types';

interface CardSummaryAnalyticProps {
  config: MetricConfig;
  filterCount: number;
  isLoading?: boolean;
}

// Get currency/unit color based on theme
const getCurrencyColor = (theme: CardTheme): string => {
  switch (theme) {
    case CardTheme.ORANGE:
      return 'text-[#F79824]'; // Invoice
    case CardTheme.PURPLE:
      return 'text-[#CB6FF6]'; // Shipment
    case CardTheme.GREEN:
      return 'text-[#16AD89]'; // Charge
    case CardTheme.TEAL:
      return 'text-[#16AD89]'; // CO2e
    case CardTheme.BLUE_LIGHT:
      return 'text-[#4476F6]'; // Volume
    case CardTheme.NEUTRAL:
      return 'text-[#CB6FF6]'; // Chargeable Weight
    case CardTheme.BLUE:
      return 'text-[#495C8C]'; // Gross Weight
    case CardTheme.RED:
      return 'text-[#e72547]'; // Net Deviations
    default:
      return 'text-neutral-100';
  }
};

export const CardSummaryAnalytic: FC<CardSummaryAnalyticProps> = ({ config, filterCount, isLoading = false }) => {
  const { title, icon, totalCount, totalAmount, filteredCount, filteredAmount, currency = 'SGD', theme, isIncludeAmout } = config;

  const currencyColor = useMemo(() => getCurrencyColor(theme), [theme]);
  const hoverShadowStyles = useMemo(() => getHoverShadowStyles({ theme, isActive: true, isEnable: true }), [theme]);

  const formatNumber = (num: number) => num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatCount = (num: number) =>
    // If the number is large, we might want to use nFormatter
    num.toLocaleString();

  // Get currency header width based on theme
  // Narrower width for weight/volume/CO2 cards
  const getCurrencyHeaderWidth = (theme: CardTheme): string => {
    const narrowThemes = [CardTheme.NEUTRAL, CardTheme.BLUE, CardTheme.TEAL, CardTheme.BLUE_LIGHT];
    return narrowThemes.includes(theme) ? 'w-[30px]' : 'w-[75px]';
  };

  // Get gradient color for decorative element based on theme
  const getGradientColor = (theme: CardTheme): string => {
    switch (theme) {
      case CardTheme.ORANGE:
        return 'rgba(247, 152, 36, 0.70)'; // #F79824 with 70% opacity
      case CardTheme.PURPLE:
        return 'rgba(203, 111, 246, 0.70)'; // #CB6FF6 with 70% opacity
      case CardTheme.GREEN:
        return 'rgba(22, 173, 137, 0.70)'; // #16AD89 with 70% opacity
      case CardTheme.TEAL:
        return 'rgba(22, 173, 137, 0.70)'; // #16AD89 with 70% opacity
      case CardTheme.BLUE_LIGHT:
        return 'rgba(68, 118, 246, 0.70)'; // #4476F6 with 70% opacity
      case CardTheme.NEUTRAL:
        return 'rgba(203, 111, 246, 0.70)'; // #CB6FF6 with 70% opacity
      case CardTheme.BLUE:
        return 'rgba(73, 92, 140, 0.70)'; // #495C8C with 70% opacity
      case CardTheme.RED:
        return 'rgba(231, 37, 71, 0.70)'; // #e72547 with 70% opacity
      default:
        return 'rgba(203, 111, 246, 0.70)';
    }
  };

  if (isLoading) {
    return <CardSkeleton showSecondRow />;
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden border border-neutral-20 rounded-8 w-full px-12 py-10 flex flex-col gap-y-2 bg-white',
        'transition-all duration-300'
      )}
      style={{
        boxShadow: '0 4px 7px 0 rgba(112, 149, 221, 0.12), 0 1px 0 0 #DFDFDF',
      }}
    >
      {/* Decorative gradient element - positioned in top-right corner */}
      <div
        className='absolute -right-[47.5px] -top-[85px] w-[120px] h-[98px] rounded-full'
        style={{
          backgroundColor: getGradientColor(theme),
          filter: 'blur(45px)',
        }}
      />
      {/* Header */}
      <div className='flex items-center justify-between border-b border-neutral-20 pb-6 mb-6'>
        <div className='flex-1 flex items-center gap-6'>
          <img src={icon} alt='' className='w-20 h-20' />
          <span className='font-medium text-neutral-100 text-12'>{title}</span>
        </div>
        {/* Header Columns - Fixed Widths matching data rows */}
        <div className='flex items-center gap-20 text-xs text-neutral-60 font-normal'>
          {isIncludeAmout ? (
            <>
              <div className=' text-left'>Count</div>
              <div className='w-[1px] h-[10px] bg-[#DFDFDF]' />
              <div className='w-[75px] text-left flex items-center gap-4'>
                <span>Amount</span> <span className={cn('font-bold ml-1', currencyColor)}>{currency}</span>
              </div>
            </>
          ) : (
            <div className={cn(getCurrencyHeaderWidth(theme), 'text-right')}>
              <span className={cn('font-bold ml-1', currencyColor)}>{currency}</span>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className='animate-pulse space-y-2'>
          <div className='h-16 bg-neutral-20 rounded w-full' />
          <div className='h-16 bg-neutral-20 rounded w-full' />
        </div>
      ) : (
        <div className='flex flex-col gap-4 text-xs'>
          {/* Total Row */}
          <div className='flex items-center justify-between'>
            <span className='flex-1 text-neutral-60 font-medium whitespace-nowrap'>Total:</span>
            <div className='flex items-center gap-20'>
              <span className='font-bold text-neutral-100  text-left' title={formatCount(totalCount)}>
                {formatCount(totalCount)}
              </span>
              {isIncludeAmout && (
                <>
                  <div className='w-[1px] h-[10px] bg-[#DFDFDF]' />
                  <span
                    className={cn('font-bold w-[75px] text-left', theme === CardTheme.RED ? getDeviationAmountColor(totalAmount) : 'text-neutral-100')}
                    title={formatNumber(totalAmount)}
                  >
                    {formatNumber(totalAmount)}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Filter Row */}
          <div className='flex items-center justify-between'>
            <span className='flex-1 text-neutral-60 font-medium whitespace-nowrap'>{filterCount} Table Filters:</span>
            <div className='flex items-center gap-20'>
              <span className='font-bold text-neutral-100  text-left' title={formatCount(filteredCount)}>
                {formatCount(filteredCount)}
              </span>
              {isIncludeAmout && (
                <>
                  <div className='w-[1px] h-[10px] bg-[#DFDFDF]' />
                  <span
                    className={cn('font-bold w-[75px] text-left', theme === CardTheme.RED ? getDeviationAmountColor(filteredAmount) : 'text-neutral-100')}
                    title={formatNumber(filteredAmount)}
                  >
                    {formatNumber(filteredAmount)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
